import TextComponent from 'Component/Render/Text';
import { EventLayoutMap } from 'Constants/Events/EventLayoutMap';
import Game from 'Game';
import { report } from 'report';
import { IGameState } from 'State/Game/IGameState';
import { IAPStateHelper } from 'State/Player/IAP/IAPStateHelper';
import { PopupID } from 'State/UI/PopupID';
import Timer from 'System/Timer/Timers/Timer';
import { ArrayHelper } from 'Utility/ArrayHelper';
import { cached } from 'Utility/async';
import DateHelper from 'Utility/DateHelper';
import { EventType } from 'Utility/Helpers/Events/EventType';
import { IOfferData } from 'Utility/Helpers/IAP/Offers/IOfferData';
import { getLayout, GLayout } from 'Utility/layout/LayoutHelper';
import { ProductId } from 'Utility/Product';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';
import { ISingleOfferTier } from 'Utility/Helpers/IAP/Offers/OfferTypes/ISingleOfferTier';
import { EVENT_OFFERS } from 'Constants/Events/EventOffers';
import { PopupOffersOfType, PopupOfferType, PopupOfferTypeMap } from 'Utility/Helpers/IAP/PopupOffers';
import { EventStateHelper } from './EventStateHelper';
import { IEventStateData } from './IEventsState';

// TODO: Cache this for each type
export function eventHasQuest(eventType: EventType): boolean {
    return Game.resource.existsOrQueued(getEventQuestsKey(eventType));
}

export function getEventQuestsKey(eventType: EventType): string {
    return `${eventType}_quests`;
}

export function getEventQuestsPath(eventType: EventType): string {
    return `resource/data/events/${eventType}/quests.json`;
}

export function queueEventOfferAssets(): void {
    const state = Game.store.getState();
    const eventWithAvailableOffer = IAPStateHelper.getHighestPrioEventWithAvailableOffer(state);
    if (eventHasOffer(eventWithAvailableOffer.type)) {
        EVENT_OFFERS[eventWithAvailableOffer.type].queueAssets();
    }
}

export function isEventType(data: unknown): data is EventType {
    return CACHED_EVENT_TYPES().includes(data as EventType);
}

// TODO: Refactor to get layouts by LayoutId
/**
 * Gets layout for highest prio event that overrides the given popupId
 */
export function getActiveEventLayout(popupId: PopupID) {
    const state = Game.store.getState();
    const validEvent = ArrayHelper.sortByHighestPriority(EventStateHelper.getActiveEvents(state)).find(
        // @ts-ignore
        event => EventLayoutMap[event.type]?.[popupId]
    );

    if (validEvent && EventLayoutMap[validEvent.type] && EventLayoutMap[validEvent.type][popupId]) {
        return getEventLayout(validEvent.type, popupId);
    }
}

/**
 * Gets the event layout for the requested popup; returns undefined if no overriding layout is found
 * @param eventType
 * @param popupId
 * @returns
 */
export function getEventLayout<ET extends EventType, PI extends PopupID>(
    eventType: ET,
    popupId: PI
): GLayout<typeof EventLayoutMap[ET][PI]> {
    return EventLayoutMap[eventType][popupId] ? getLayout(EventLayoutMap[eventType][popupId]) : undefined;
}

/** Returns the offer productId for the given event if there is one, else returns undefined */
export function getEventOfferProductId(
    state: IGameState,
    eventType: keyof PopupOffersOfType<PopupOfferType.EVENT_OFFER>
): ProductId | undefined {
    const ids = EVENT_OFFERS?.[eventType]?.productId ?? [];
    const isOfferMultiTier = EVENT_OFFERS[eventType].offer.tiers.length > 1;
    if (isOfferMultiTier) {
        if (!ids.some(id => IAPStateHelper.hasPurchasedProduct(state, id))) {
            const offerTier = IAPHelper.getRelevantOfferTier(state, EVENT_OFFERS[eventType].offer);
            const id = (offerTier as ISingleOfferTier).productId;

            return !IAPStateHelper.hasPurchasedProduct(state, id) ? id : undefined;
        }
    } else {
        for (let i = 0; i < ids.length - 1; i++) {
            if (IAPStateHelper.hasPurchasedProduct(state, ids[i])) continue;

            return ids[i];
        }
    }
    return;
}

/** Returns the offer for the given event if there is one, else returns undefined */
export function getEventOfferData(
    eventType: keyof PopupOffersOfType<PopupOfferType.EVENT_OFFER>
): IOfferData | undefined {
    return EVENT_OFFERS?.[eventType]?.offer;
}

export function eventHasOffer(eventType: EventType): eventType is keyof PopupOffersOfType<PopupOfferType.EVENT_OFFER> {
    return CACHED_EVENT_OFFER_KEYS().includes(eventType);
}

export const CACHED_EVENT_OFFER_KEYS = cached(() => {
    const eventOffers = PopupOfferTypeMap[PopupOfferType.EVENT_OFFER];
    return Object.keys(eventOffers).filter(key => eventOffers.hasOwnProperty(key));
});

export function getEventOfferButtonCacheKey(eventType: EventType): string {
    return `${eventType}-button`;
}

export function createEventOfferTimer(
    state: IGameState,
    eventType: EventType,
    textComponent: TextComponent,
    options?: { onUpdate?: (state: IGameState) => void, onComplete?: (state: IGameState) => void }
): Timer {
    const timer = new Timer(false);

    const eventState = EventStateHelper.getEventState(state, eventType);

    if (!eventState || !EventStateHelper.isEventActive(state, eventType)) {
        report('Failed to find active event when creating an event timer', {
            once: true,
            sampleRate: 0.1,
            tags: {
                eventType,
            },
            fingerprint: 'createEventOfferTimer:NoEventFound',
            state,
        });
        return timer;
    }

    const { duration } = eventState;

    // Set up timing
    timer.duration = duration;
    timer.getDate = (state: IGameState) => {
        const eventState = EventStateHelper.getEventState(state, eventType);
        if (eventState) {
            return DateHelper.getDate(eventState.startTime);
        }

        return null;
    };

    // Update timer text
    timer.format = 'DD:hh:mm:ss';
    timer.onUpdate = (state: IGameState) => {
        if (textComponent && textComponent.gameObject && !textComponent.gameObject.destroyed) {
            textComponent.text = timer.getFormattedCountdown(state);
        }

        if (options?.onUpdate) {
            options.onUpdate(state);
        }
    };
    timer.onUpdate(state);

    if (options?.onComplete) {
        timer.complete = options.onComplete;
    }

    return timer;
}

export function getAllEvents(): IEventStateData[] {
    const activeEvents: IEventStateData[] = [];

    const config = Game.resource.get<IEventConfig[]>('events');
    config.forEach(config => {
        const startTime = DateHelper.getDate(config.startTime).toISOString();

        if (config.enabled)
            activeEvents.push({
                ...config,
                startTime,
            });
    });

    return activeEvents;
}
/**
 * Checks that all the event assets are loaded, returns false if there are any assets that are not loaded
 * @param event
 */
export function areEventAssetsLoaded(event: IEventStateData): boolean {
    return !event.assets.some(asset => {
        return !Game.resource.existsAndLoaded(asset);
    });
}

export interface IEventConfig {
    type: EventType;
    duration: number;
    startTime: string;
    hasQuests: boolean;
    requiredLevel?: number;
    enabled?: boolean;
}
const CACHED_EVENT_TYPES = cached<EventType[]>(() => {
    return Object.values(EventType);
});
